import { useMemo, type PropsWithChildren } from 'react';
import { map } from 'rxjs/operators';
import { SecuritiesContext } from '../../contexts/SecuritiesContext';
import { useCachedSubscription } from '../../hooks/useCachedSubscription';
import { wsSubscriptionMemory } from '../../pipes/wsSubscriptionMemory';
import { SECURITY } from '../../tokens/requestTypes';
import type { Security } from '../../types/Security';
import { getSearchSymbol } from '../../utils';
import { useCFDIndexes } from './cfdIndexes';
import { useFutureIndexes } from './futureIndexes';
import { useOptionIndexes } from './optionIndexes';
import { useOTCIndexes } from './otcIndexes';
import { usePerpIndexes } from './perpIndexes';
import { usePredictionIndexes } from './predictionIndexes';
import { useCoreSecurityIndexes } from './SecuritiesProviderIndexes';
import { useSpotIndexes } from './spotIndexes';
import { isValidSecurity } from './utils';

export {
  getPredictionOutcomeEventID,
  UNLINKED_PREDICTION_EVENT_ID,
  type PredictionSecurityIndexes,
} from './predictionIndexes';
export {
  getCurrencyPairFromSecurity,
  getExpirationByMarketByCurrencyIdentity,
  getFuturesByMarketByCurrencyPair,
  getStrikesByOptionKey,
  isActiveSecurity,
} from './SecuritiesProviderIndexes';

const SECURITY_REQUEST = { name: SECURITY, tag: 'SecuritiesProvider' } as const;

export const SecuritiesProvider = function SecuritiesProvider({ children }: PropsWithChildren) {
  const { data: subscription } = useCachedSubscription<Security>(SECURITY_REQUEST);

  // The provider owns one shared memory stream. Each grouped index hook derives
  // its own observable from the latest subscription message.
  const securityMemoryObs = useMemo(
    () =>
      subscription.pipe(
        map(message => {
          const securities = (message.data ?? []).filter(isValidSecurity);
          for (const security of securities) {
            security.searchSymbol = getSearchSymbol(security.Symbol);
          }
          return { ...message, data: securities };
        }),
        wsSubscriptionMemory(security => security.Symbol)
      ),
    [subscription]
  );

  const coreIndexes = useCoreSecurityIndexes(securityMemoryObs);
  const perpIndexes = usePerpIndexes(securityMemoryObs);
  const cfdIndexes = useCFDIndexes(securityMemoryObs);
  const futureIndexes = useFutureIndexes(securityMemoryObs);
  const optionIndexes = useOptionIndexes(securityMemoryObs);
  const spotIndexes = useSpotIndexes(securityMemoryObs);
  const otcIndexes = useOTCIndexes(securityMemoryObs);
  const predictionIndexes = usePredictionIndexes(securityMemoryObs);

  const contextValue = useMemo(
    () => ({
      ...coreIndexes,
      securityMemoryObs,
      searchableSecurities: coreIndexes.searchableSecurities!,
      ...perpIndexes,
      ...cfdIndexes,
      ...futureIndexes,
      ...optionIndexes,
      ...spotIndexes,
      ...otcIndexes,
      ...predictionIndexes,
    }),
    [
      cfdIndexes,
      coreIndexes,
      futureIndexes,
      optionIndexes,
      otcIndexes,
      perpIndexes,
      predictionIndexes,
      securityMemoryObs,
      spotIndexes,
    ]
  );

  return <SecuritiesContext.Provider value={contextValue}>{children}</SecuritiesContext.Provider>;
};
